import React, { useCallback, useMemo, useRef } from 'react';
import { Icon, Spinner, Stack, Tooltip } from '@servicetitan/design-system';
import useUserStatus from '@modules/UserStatusModule/hooks/useUserStatus';
import useOnClickOutside from '@lib/hooks/useOnClickOutside';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import UserStatusModule from '@modules/UserStatusModule';
import UserStatusDisplay from './UserStatusDisplay';
import useCountdownTimer from '@lib/hooks/useCountDownTimer';
import { UserSetAvailabilityStatus } from '@api/generated/graphql';
import statusValues, {
  StatusBackgroundColor,
  StatusDotColor,
} from '@modules/UserStatusModule/statusValues';
import classNames from 'classnames';
import styles from './UserStatusMenu.module.css';
import StatusLight from '@components/StatusLight/StatusLight';
import { mainLayoutLocators } from '@locators/main-layout';
import CallingDisabledGuard from '@components/CallingDisabledGuard/CallingDisabledGuard';
import useCallingDisabled from '@lib/hooks/useCallingDisabled';
import usePhoneState from '@modules/SoftPhoneModule/usePhoneState';
import { hasValidSeat, WRAPUP_LABEL } from 'src/constants/user';
import { useCurrentUserStatus } from '@modules/ServiceModule/hooks';
import { getTestIdProps } from '@lib/testUtils';
import { FeatureFlagKeys, useFeatureFlags } from '@modules/FeatureFlagModule';
import strings from '@modules/UserStatusModule/constants/strings';

// we don't want to show the automated queue wrap up status (aka Recovery) in the dropdown
const userStatusOptions = Object.entries(statusValues)
  .filter(([value]) => value !== WRAPUP_LABEL)
  .map(([value, text]) => ({
    text,
    value,
  }));

/** Displays and updates the status of the current user via a dropdown like menu */
export function UserStatusMenu() {
  const { user } = useAuth();
  const { callingDisabled } = useCallingDisabled();
  const { phoneConnected } = usePhoneState();
  const {
    [FeatureFlagKeys.featureFeUserStatusPhase2]: featureFeUserStatusPhase2,
  } = useFeatureFlags();
  const [open, setOpen] = React.useState(false);
  const { recoveryTimeEndsAt } = useUserStatus();
  const { status: userStatus, loading, canChange } = useCurrentUserStatus();

  const clickOutsideRef = useRef<HTMLDivElement>(null);
  const clickOutsideIgnoreRef = useRef<HTMLButtonElement>(null);
  useOnClickOutside(
    clickOutsideRef,
    () => setOpen(false),
    clickOutsideIgnoreRef,
  );

  const { count } = useCountdownTimer(recoveryTimeEndsAt ?? 0);
  const isWrapUp = count > 0;

  // Check if user has a seat (Basic or Advanced) for status changes
  const hasSeat = hasValidSeat(user?.seatLevel);

  // set the status as wrap up if still in automated queue status (aka Recovery)
  const status = isWrapUp ? WRAPUP_LABEL : userStatus;
  const disabled = featureFeUserStatusPhase2
    ? loading || !hasSeat
    : isWrapUp || loading || !canChange || !hasSeat;

  const onChange = useCallback(
    (status: string) => {
      UserStatusModule.updateUserStatus(
        user?.id || '',
        status as UserSetAvailabilityStatus,
      );
      setOpen(false);
    },
    [user],
  );

  const tooltipText = useMemo(() => {
    if (!hasSeat) {
      return strings.tooltips.noSeat;
    }
    if (featureFeUserStatusPhase2) {
      // In phase 2, only show tooltip for seat requirement
      return '';
    }
    if (isWrapUp) {
      return strings.tooltips.wrapUp;
    }
    if (!canChange) {
      return strings.tooltips.cannotChange;
    }
    return '';
  }, [isWrapUp, canChange, hasSeat, featureFeUserStatusPhase2]);

  return (
    <div className='position-relative'>
      <Tooltip text={tooltipText} portal direction='bottom'>
        <button
          disabled={disabled}
          onClick={() => {
            setOpen(!open);
          }}
          ref={clickOutsideIgnoreRef}
          className={classNames(
            styles.userStatusDropdownButton,
            'border-radius-pill',
            { [styles.userStatusDropdownButtonDisabled]: disabled },
          )}
          style={
            {
              '--status-background-color': StatusBackgroundColor[status],
            } as React.CSSProperties
          }
          {...getTestIdProps(mainLayoutLocators.navBar.userStatusMenu)}
        >
          <Stack justifyContent='space-around' alignItems='center'>
            <Stack.Item fill>
              <Stack alignItems='center'>
                <Icon
                  className='m-r-half'
                  name='shape_circle'
                  color={StatusDotColor[status]}
                  el='span'
                  size={14}
                />
                <UserStatusDisplay
                  status={status}
                  recoveryTimeInSeconds={count}
                />
              </Stack>
            </Stack.Item>
            {/* display expand icons if not loading */}
            {loading ? (
              <Spinner
                data-testid='user-status-loader'
                color={StatusDotColor[status]}
                el='span'
                className={styles.spinner}
              />
            ) : open ? (
              <Icon
                name='expand_less'
                color={StatusDotColor[status]}
                el='span'
              />
            ) : (
              <Icon
                name='expand_more'
                color={StatusDotColor[status]}
                el='span'
              />
            )}
          </Stack>
        </button>
        <div
          className={classNames(
            styles.userStatusDropdownMenu,
            open ? 'd-f' : 'd-n',
          )}
          ref={clickOutsideRef}
        >
          <Stack
            direction='column'
            spacing={1}
            alignItems='flex-start'
            justifyContent='center'
          >
            {userStatusOptions.map((option) => {
              const menuOption = (
                <StatusLight
                  key={option.value}
                  onClick={() => onChange(option.value)}
                  color={StatusDotColor[option.value]}
                  className={styles.userStatusDropdownMenuItem}
                  {...getTestIdProps(
                    mainLayoutLocators.navBar.userStatusMenuOption,
                  )}
                >
                  {option.text}
                </StatusLight>
              );
              if (option.value === UserSetAvailabilityStatus.Available) {
                return (
                  <CallingDisabledGuard
                    key={option.value}
                    additionalDisabled={
                      (callingDisabled || !phoneConnected) &&
                      option.value === UserSetAvailabilityStatus.Available
                    }
                  >
                    {menuOption}
                  </CallingDisabledGuard>
                );
              } else {
                return menuOption;
              }
            })}
          </Stack>
        </div>
      </Tooltip>
    </div>
  );
}
